import { useEffect, useState } from "react";

import WorkspaceHeader from "../workspaces/WorkspaceHeader";
import WorkspaceNavigation from "../workspaces/WorkspaceNavigation";

import WorkspaceOverview from "../overview/WorkspaceOverview";
import WorkspaceItems from "../workspaces/WorkspaceItems";
import WorkspaceMembers from "../workspaces/WorkspaceMembers";

import EditWorkspaceDrawer from "../edit/EditWorkspaceDrawer";
import AssignTaskDrawer from "../workspaces/AssignTaskDrawer";

import { getWorkspaceDetails } from "../services/workspaceService";

const WorkspaceDrawer = ({
  open,
  onClose,
  workspace,
  project,
}) => {
  const [activeTab, setActiveTab] =
    useState("Overview");

  const [workspaceDetails, setWorkspaceDetails] =
    useState(null);

  const [loading, setLoading] =
    useState(false);

  const [error, setError] =
    useState("");

  const [openEditDrawer, setOpenEditDrawer] =
    useState(false);

  const [openAssignTaskDrawer, setOpenAssignTaskDrawer] =
    useState(false);

  /**
   * ==========================================================
   * IMPORTANT
   *
   * Changing this value causes WorkspaceItems to fetch again.
   * ==========================================================
   */
  const [itemsRefreshKey, setItemsRefreshKey] =
    useState(0);

  /**
   * ==========================================================
   * PROJECT UUID
   * ==========================================================
   */

  const projectUuid =
    project?.uuid ||
    project?.project_uuid ||
    project?.projectUuid;

  /**
   * ==========================================================
   * WORKSPACE UUID
   * ==========================================================
   */

  const workspaceUuid =
    workspace?.uuid ||
    workspace?.workspace_uuid ||
    workspace?.id;

  /**
   * ==========================================================
   * FETCH WORKSPACE DETAILS
   * ==========================================================
   */

  const fetchWorkspaceDetails = async () => {
    if (!projectUuid || !workspaceUuid) {
      console.warn(
        "Project UUID or Workspace UUID missing"
      );

      return;
    }

    try {
      setLoading(true);
      setError("");

      console.log(
        "========== FETCH WORKSPACE DETAILS =========="
      );

      console.log(
        "PROJECT UUID:",
        projectUuid
      );

      console.log(
        "WORKSPACE UUID:",
        workspaceUuid
      );

      const response =
        await getWorkspaceDetails(
          projectUuid,
          workspaceUuid
        );

      console.log(
        "WORKSPACE DETAIL API:",
        response
      );

      const data =
        response?.data || response;

      setWorkspaceDetails(data);

    } catch (error) {

      console.error(
        "FAILED TO FETCH WORKSPACE DETAILS:",
        error
      );

      setError(
        error.response?.data?.message ||
          "Failed to load workspace details."
      );

      setWorkspaceDetails(null);

    } finally {
      setLoading(false);
    }
  };

  /**
   * ==========================================================
   * FETCH WHEN DRAWER OPENS
   * ==========================================================
   */

  useEffect(() => {
    if (!open) {
      return;
    }

    fetchWorkspaceDetails();

  }, [
    open,
    projectUuid,
    workspaceUuid,
  ]);

  /**
   * ==========================================================
   * CLOSE DRAWER
   * ==========================================================
   */

  const handleClose = () => {

    setWorkspaceDetails(null);

    setError("");

    setActiveTab("Overview");

    setOpenEditDrawer(false);

    setOpenAssignTaskDrawer(false);

    onClose();
  };

  /**
   * ==========================================================
   * ITEM CREATED SUCCESSFULLY
   *
   * THIS IS THE IMPORTANT PART.
   * ==========================================================
   */

  const handleItemCreated = async (createdItem) => {

    console.log(
      "================================================"
    );

    console.log(
      "ITEM CREATED SUCCESSFULLY"
    );

    console.log(
      "CREATED ITEM:",
      createdItem
    );

    console.log(
      "REFRESHING ITEMS..."
    );

    console.log(
      "================================================"
    );

    /**
     * Increase refresh key.
     *
     * WorkspaceItems receives:
     *
     * refreshKey={itemsRefreshKey}
     *
     * So useEffect inside WorkspaceItems
     * will execute again.
     */

    setItemsRefreshKey(
      (previousKey) => previousKey + 1
    );

    /**
     * Refresh workspace details too.
     */

    await fetchWorkspaceDetails();

    /**
     * Close create item drawer.
     */

    setOpenAssignTaskDrawer(false);

    /**
     * Automatically move user to Items tab.
     */

    setActiveTab("Items");
  };

  /**
   * ==========================================================
   * WORKSPACE UPDATED
   * ==========================================================
   */

  const handleWorkspaceUpdated = async () => {

    console.log(
      "WORKSPACE UPDATED -> REFRESHING DETAILS"
    );

    await fetchWorkspaceDetails();

    setOpenEditDrawer(false);
  };

  /**
   * ==========================================================
   * DON'T RENDER WHEN CLOSED
   * ==========================================================
   */

  if (!open || !workspace) {
    return null;
  }

  /**
   * ==========================================================
   * CURRENT WORKSPACE
   * ==========================================================
   */

  const currentWorkspace =
    workspaceDetails || workspace;

  return (
    <>
      {/* ======================================================
          BACKDROP
          ====================================================== */}

      <div
        className="fixed inset-0 z-50 bg-black/40"
        onClick={handleClose}
      />

      {/* ======================================================
          WORKSPACE DRAWER
          ====================================================== */}

      <div
        className="
          fixed
          right-0
          top-0
          z-[60]
          flex
          h-screen
          w-full
          flex-col
          border-l
          border-slate-200
          bg-white
          shadow-2xl
          md:w-[80%]
          lg:w-[70%]
          xl:w-[50%]
        "
      >

        {/* ==================================================
            HEADER
            ================================================== */}

        <WorkspaceHeader
          workspace={currentWorkspace}
          project={project}
        />

        {/* ==================================================
            NAVIGATION
            ================================================== */}

        <WorkspaceNavigation
          activeTab={activeTab}
          setActiveTab={setActiveTab}
        />

        {/* ==================================================
            CONTENT
            ================================================== */}

        <div className="flex-1 overflow-y-auto bg-slate-50 p-6">

          {/* =================================================
              LOADING
              ================================================= */}

          {loading && !workspaceDetails && (
            <div className="mb-4 rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-700">
              Loading workspace details...
            </div>
          )}

          {/* =================================================
              ERROR
              ================================================= */}

          {error && (
            <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              {error}
            </div>
          )}

          {/* =================================================
              OVERVIEW
              ================================================= */}

          {activeTab === "Overview" && (
            <WorkspaceOverview
              workspace={currentWorkspace}
              project={project}
              onEdit={() =>
                setOpenEditDrawer(true)
              }
            />
          )}

          {/* =================================================
              ITEMS
              ================================================= */}

          {activeTab === "Items" && (
            <WorkspaceItems
              workspace={currentWorkspace}
              onAssignTask={() =>
                setOpenAssignTaskDrawer(true)
              }
              refreshKey={itemsRefreshKey}
            />
          )}

          {/* =================================================
              MEMBERS
              ================================================= */}

          {activeTab === "Members" && (
            <WorkspaceMembers
              workspace={currentWorkspace}
            />
          )}


    

        </div>

      </div>

      {/* ======================================================
          EDIT WORKSPACE DRAWER
          ====================================================== */}

      <EditWorkspaceDrawer
        open={openEditDrawer}
        workspace={currentWorkspace}
        project={project}
        onClose={() =>
          setOpenEditDrawer(false)
        }
        onUpdated={handleWorkspaceUpdated}
      />

      {/* ======================================================
          CREATE / ASSIGN ITEM DRAWER
          ====================================================== */}

      <AssignTaskDrawer
        open={openAssignTaskDrawer}
        workspace={currentWorkspace}
        project={project}
        onClose={() =>
          setOpenAssignTaskDrawer(false)
        }
        onCreated={handleItemCreated}
      />

    </>
  );
};

export default WorkspaceDrawer;